import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import { Badge, Button, Stack, Box } from '@mui/material';
import useUsersReport from './_data_sources/useUsersReport';
import { AppContext } from 'views/App/context';
import datetimeHelper from 'helpers/datetimeHelper';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { QuickSearch } from 'components/_atoms';
import { UserListResult } from 'views/App/Portal/Reporting/Users/_types/users';
import UsersFilters from './Filters';
import useUsersReportExport from './_data_sources/useUsersReportExport';
import { enqueueSnackbar } from 'notistack';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { v4 as uuidv4 } from 'uuid';

export type UsersListParams = {
  createdAtStart?: string | null;
  createdAtEnd?: string | null;
  search?: string | null;
  groups?: string[] | null;
  lastLoginAtStart?: string | null;
  lastLoginAtEnd?: string | null;
  roles?: string[] | null;
  status?: string | null;
};

const defaultParams: UsersListParams = {
  createdAtStart: null,
  createdAtEnd: null,
  search: null,
  groups: [],
  lastLoginAtStart: null,
  lastLoginAtEnd: null,
  roles: [],
  status: null,
};

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = React.useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);
  const [results, setResults] = useState<UserListResult[]>([]);
  const { generate: generateExport } = useUsersReportExport();

  const updateParams = useCallback(
    (updatedParams: UsersListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const newParams = _.omitBy({ ...params }, _.isEmpty);
    if (newParams.status) {
      newParams.active = newParams.status === 'ACTIVE';
      delete newParams.status;
    }

    return newParams;
  }, [params]);

  const isFiltered = !_.isEmpty(queryParams);

  const reportQuery = useUsersReport(currentOrganizationId, queryParams);

  const handleExportClick = useCallback(() => {
    generateExport(currentOrganizationId, queryParams, () => {
      enqueueSnackbar(parse(t(`reporting.users.retrievalConfirmation`, '', {})), {
        variant: 'success',
        autoHideDuration: 6000,
      });
      window.setTimeout(() => {
        queryClient.invalidateQueries('notifications_unread_count');
      }, 15000);
    });
  }, [queryClient, currentOrganizationId, queryParams, t]);

  useEffect(() => {
    if (reportQuery.data && !reportQuery.isError) {
      const fetchedResults = reportQuery.data.pages.flatMap((page) => page.content) || [];
      setResults(fetchedResults);
    }
  }, [reportQuery.data, reportQuery.isError]);

  const columns: ColumnDef = useMemo(
    () => ({
      firstName: {
        header: t('reporting.users.columns.firstName'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.firstName}</span>;
        },
      },
      lastName: {
        header: t('reporting.users.columns.lastName'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.lastName}</span>;
        },
      },
      email: {
        header: t('reporting.users.columns.email'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.email}</span>;
        },
      },
      status: {
        header: t('reporting.users.columns.status'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Badge
              variant='inline'
              badgeContent={row.active ? t('reporting.users.status.ACTIVE') : t('reporting.users.status.INACTIVE')}
              color={row.active ? 'success' : 'error'}
              sx={{ textTransform: 'uppercase' }}
            />
          );
        },
      },
      lastLoginAt: {
        header: t('reporting.users.columns.lastLoginAt'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDateTime(row.lastLoginAt)}</span>;
        },
      },
    }),
    [t]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('reporting.users.title'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <QuickSearch
              id='users_search'
              initialValue={params.search || ''}
              onChange={(val) => updateParams({ search: val?.trim() })}
              placeholder={t('reporting.users.search.placeholder')}
            />
          }
          rightItems={
            <Stack
              direction='row'
              spacing={2}
            >
              {isFiltered && (
                <Button
                  color='neutral'
                  onClick={() => clearParams()}
                  size='small'
                >
                  {t('reporting.users.actions.clearFilters')}
                </Button>
              )}
              <UsersFilters
                key={`users_filters_${JSON.stringify(params)}`}
                organizationId={currentOrganizationId}
                params={params}
                updateParams={updateParams}
              />
              <Button
                size='small'
                variant='outlined'
                onClick={() => handleExportClick()}
              >
                {t('reporting.users.actions.export')}
              </Button>
            </Stack>
          }
        />
        <GridSection
          columns={columns}
          data={results}
          query={reportQuery}
          gridProps={{
            getRowId: (row: UserListResult) => uuidv4(),
          }}
        />
      </Box>
    </CommonLayout>
  );
};

export default Users;
